import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Search } from "lucide-react";

interface Term {
  term: string;
  category: "Memória" | "Persona/ToM" | "Métricas" | "Runtime" | "Anti-Alucinação" | "Skills";
  short: string;
  detail: string;
}

const TERMS: Term[] = [
  // Memória
  { term: "Working Memory (WM)", category: "Memória", short: "Memória curta do turno atual.", detail: "Estado conversacional ativo: subflow, slots preenchidos, último ato do usuário/SYRA. TTL de ~30min. Equivalente à RAM da conversa." },
  { term: "Long-Term Memory (LTM)", category: "Memória", short: "Memórias consolidadas e persistentes.", detail: "Fatos, preferências e padrões extraídos ao longo do tempo. Indexados por embedding para busca semântica." },
  { term: "Pattern Separation", category: "Memória", short: "Distinguir contextos semelhantes.", detail: "Mecanismo que evita confundir memórias parecidas (ex.: 2 reuniões diferentes no mesmo dia)." },
  { term: "Consolidação", category: "Memória", short: "Promoção de candidato a memória oficial.", detail: "Etapa em que um candidato (extraído do turno) vira memória persistente após reforço/multi-author." },
  { term: "Decay", category: "Memória", short: "Esquecimento gradual.", detail: "Confiança da memória diminui com o tempo se não for reforçada — evita conhecimento desatualizado." },
  // Persona/ToM
  { term: "ToM (Theory of Mind)", category: "Persona/ToM", short: "Modelo do que o usuário está pensando/sentindo.", detail: "Inferência sobre intenção, humor e expectativa do usuário, usada para modular o tom da resposta." },
  { term: "Persona Modulator", category: "Persona/ToM", short: "Ajusta tom da SYRA pelo contexto.", detail: "Aplica direcionamento de estilo (mais formal, mais direto, mais empático) com base no ToM e no histórico do grupo." },
  { term: "Persona Drift", category: "Persona/ToM", short: "Desvio gradual do tom configurado.", detail: "Detector que alerta quando a persona da SYRA está se afastando da configuração base do agente." },
  { term: "ToM Directives", category: "Persona/ToM", short: "Instruções derivadas do ToM.", detail: "Linhas de orientação adicionadas ao prompt (ex.: 'usuário parece frustrado, evite jargão')." },
  // Métricas
  { term: "Brier Score", category: "Métricas", short: "Mede precisão de previsões probabilísticas.", detail: "Quanto menor, melhor. 0 = previsão perfeita; 0.25 = chute aleatório. Aplicado em calibração de confiança." },
  { term: "ECE (Expected Calibration Error)", category: "Métricas", short: "Diferença entre confiança declarada e acerto real.", detail: "Se a SYRA diz '90% certeza' e acerta só 70% das vezes, o ECE captura esse gap." },
  { term: "p95 / p99", category: "Métricas", short: "Latência do 95º/99º percentil.", detail: "95% das requisições são mais rápidas que esse valor. p99 é mais sensível a outliers." },
  { term: "Contribution Score", category: "Métricas", short: "Quanto o ToM melhora a resposta.", detail: "Comparação A/B (com ToM vs. sem) para medir se a inferência psicológica realmente ajuda." },
  // Runtime
  { term: "Shadow Mode", category: "Runtime", short: "Coleta dados sem afetar o usuário.", detail: "Feature roda em paralelo ao caminho legacy, grava telemetria, mas a resposta entregue continua sendo do legacy. Etapa de validação antes de canary." },
  { term: "Canary", category: "Runtime", short: "Rollout gradual de feature nova.", detail: "Liga a feature para 5% → 25% → 50% → 100% do tráfego, monitorando regressões a cada degrau." },
  { term: "Feature Flag", category: "Runtime", short: "Liga/desliga código sem deploy.", detail: "Tem `enabled` (ON/OFF) e `rollout_pct` (0-100). Se enabled=true mas pct=0, a flag é placebo." },
  { term: "Correlation ID", category: "Runtime", short: "Identificador para rastrear um turno fim-a-fim.", detail: "Mesmo ID aparece em logs de webhook, gateway, retrieval, composer e envio — permite reconstituir o fluxo." },
  { term: "Fail-open", category: "Runtime", short: "Em caso de erro, segue o caminho legacy.", detail: "Se a feature nova falha, a SYRA não trava — usa o comportamento antigo. Oposto: fail-closed (bloqueia)." },
  { term: "Fallback Silencioso", category: "Runtime", short: "Caiu para legacy sem registrar.", detail: "Bug perigoso: a feature nova falha mas ninguém é notificado. Detectado por correlation_id ausente." },
  // Anti-Alucinação
  { term: "Anti-Hallucination Shield", category: "Anti-Alucinação", short: "Bloqueia resposta sem evidência.", detail: "Verifica se a resposta tem grounding em memórias/contexto antes de enviar. Se não tem, recusa ou pede clarificação." },
  { term: "Grounding", category: "Anti-Alucinação", short: "Citação de fonte interna.", detail: "Toda afirmação factual deve ter origem em memória, fato do grupo ou busca web — caso contrário é alucinação." },
  { term: "Self-Obsession Shortcircuit", category: "Anti-Alucinação", short: "Evita SYRA falar sobre si mesma sem necessidade.", detail: "Detecta turnos onde a SYRA estaria centrando a resposta nela própria e corrige o foco para o usuário." },
  // Skills
  { term: "Skill Matcher", category: "Skills", short: "Escolhe a habilidade certa para o intent.", detail: "Roteador entre skills (chat, search, summarize, schedule, etc.) com base no intent classificado." },
  { term: "Metacog Gate", category: "Skills", short: "Verifica se a skill foi bem executada.", detail: "Camada de meta-cognição que decide se a saída da skill passa o critério de qualidade antes de virar resposta." },
  { term: "Invocation Window", category: "Skills", short: "Janela em que a SYRA pode responder.", detail: "Heurística para decidir se o turno atual merece resposta (vs. silenciar) baseado em sinais sociais e mention." },
];

const CATEGORIES: Term["category"][] = ["Memória", "Persona/ToM", "Métricas", "Runtime", "Anti-Alucinação", "Skills"];

export default function Glossario() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<Term["category"] | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TERMS.filter((t) => {
      if (activeCat && t.category !== activeCat) return false;
      if (!q) return true;
      return (
        t.term.toLowerCase().includes(q) ||
        t.short.toLowerCase().includes(q) ||
        t.detail.toLowerCase().includes(q)
      );
    });
  }, [query, activeCat]);

  return (
    <div className="container max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-start gap-3">
        <BookOpen className="h-7 w-7 text-primary mt-1" />
        <div>
          <h1 className="text-3xl font-bold">Glossário SYRA</h1>
          <p className="text-muted-foreground mt-1">
            Termos técnicos traduzidos. Use para entender labels e dashboards sem virar engenheiro.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar termo..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={activeCat === null ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setActiveCat(null)}
          >
            Todas
          </Badge>
          {CATEGORIES.map((cat) => (
            <Badge
              key={cat}
              variant={activeCat === cat ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setActiveCat(cat)}
            >
              {cat}
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid gap-3">
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Nenhum termo encontrado para "{query}".
            </CardContent>
          </Card>
        ) : (
          filtered.map((t) => (
            <Card key={t.term}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-lg">{t.term}</CardTitle>
                  <Badge variant="secondary" className="text-xs">{t.category}</Badge>
                </div>
                <p className="text-sm font-medium text-foreground/90">{t.short}</p>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground">{t.detail}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center pt-4">
        {filtered.length} termo{filtered.length !== 1 ? "s" : ""} • {TERMS.length} no total
      </p>
    </div>
  );
}
